import DialogScraperSettingsJsonField from './DialogScraperSettingsJsonField';
import GatewaySettingsForm from './gatewaySettings/GatewaySettingsForm';
import { schemaFor, setAt, settingsProblems, withoutKeys } from '../../utils/gatewaySettings';
import i18n from '../../i18n';

const FORM_MODE = 'form';

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * A gateway settings row's settings as a form of its driver's settings.
 *
 * Core describes the settings of each driver — one per gateway API and kind — from
 * its settings class, and GatewaySettingsPage loads them once into the store
 * (`gatewaySettingsSchemas`). When the kind and API picked in the form have a
 * driver with a schema, **Form** is the editor: one input per key the driver reads
 * (GatewaySettingsForm), each with the problem the cabinet would refuse it for.
 * Fields and Scenarios are not offered there — Form covers both, an xscraper
 * action with the same widget Scenarios uses, its code and input editable. Raw
 * stays, for anything the form does not show.
 *
 * Secrets come back masked (`tok***def`); a mask left in place keeps the stored
 * value when saved, so Raw shows the masks too, and leaving them is fine.
 *
 * With no kind or API chosen, an API no driver of the kind answers to, or the
 * schemas not loaded, this is DialogScraperSettingsJsonField as it was.
 */
export default class DialogGatewaySettingsField extends DialogScraperSettingsJsonField {
    constructor(props) {
        super(props);
        // The base's handlers are instance fields, so they are wrapped here rather
        // than overridden: Form is switched to directly, and leaving it by hand, or
        // typing in Raw, stops followSchema pulling the operator back into it.
        const switchMode = this.handleModeChange;
        this.handleModeChange = (event, mode) => {
            if (!mode || mode === this.state.mode) return;
            if (mode === FORM_MODE) {
                this.setState({ mode: FORM_MODE, formLeft: false });
                return;
            }
            this.setState({ formLeft: true });
            switchMode(event, mode);
        };
        const rawChange = this.handleRawChange;
        this.handleRawChange = (event) => {
            this.setState({ formLeft: true });
            rawChange(event);
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const next = DialogScraperSettingsJsonField.getDerivedStateFromProps(nextProps, prevState);
        // The base falls back to Raw for a value it cannot show as Fields — most
        // settings. A reset or another record keeps the form open while it can be.
        if (next && prevState.mode === FORM_MODE && isPlainObject(nextProps.value)) {
            return { ...next, mode: FORM_MODE };
        }
        return next;
    }

    componentDidMount() {
        this.followSchema();
        // The schemas load with the page; a dialog opened first catches up here.
        this.unsubscribe = this.store()?.subscribe?.(() => {
            this.followSchema();
            this.forceUpdate();
        });
    }

    componentDidUpdate() {
        super.componentDidUpdate();
        this.followSchema();
    }

    componentWillUnmount() {
        this.unsubscribe?.();
    }

    schema() {
        const state = this.store()?.getState();
        const { data } = this.props;
        return schemaFor(data?.kind, data?.gatewayApiId, state?.controllersData?.publicGatewayApis, state?.gatewaySettingsSchemas);
    }

    /** Whether Form can show the value: a schema, and a value that is an object (or nothing yet). */
    formOffered() {
        const { value } = this.props;
        return Boolean(this.schema()) && (value === null || value === undefined || isPlainObject(value));
    }

    /**
     * Opens on the form whenever there is one, and leaves it when the kind or API
     * is changed to one without — for Raw, which always is. Raw chosen by hand, or
     * typed in, is left alone.
     */
    followSchema() {
        const { mode, formLeft } = this.state;
        if (this.formOffered() && mode !== FORM_MODE && !formLeft) {
            this.setState({ mode: FORM_MODE });
        } else if (!this.formOffered() && mode === FORM_MODE) {
            this.setState({ mode: 'raw' });
        }
    }

    fieldsModeOffered() {
        return !this.schema() && super.fieldsModeOffered();
    }

    extraModes() {
        if (!this.schema()) return super.extraModes();
        return [{ value: FORM_MODE, label: i18n.t('text.jsonModeForm'), disabled: !this.formOffered() }];
    }

    helperLine(mode, fieldsPossible) {
        if (mode !== FORM_MODE) return super.helperLine(mode, fieldsPossible);
        const schema = this.schema();
        return this.props.error || i18n.t('gatewaySettings.formHelper', {
            api: schema?.api,
            kind: schema?.kind ? i18n.t(`enums.${schema.kind}`) : ''
        });
    }

    handleChangeAt = (path, next) => {
        const current = isPlainObject(this.props.value) ? this.props.value : {};
        this.emitValue(setAt(current, path, next));
    };

    handleRemoveKeys = (keys) => {
        this.emitValue(withoutKeys(this.props.value, keys));
    };

    renderModeBody(mode) {
        if (mode !== FORM_MODE) return super.renderModeBody(mode);
        const schema = this.schema();
        if (!schema) return null;

        const { field, data } = this.props;
        return (
            <GatewaySettingsForm
                schema={schema}
                value={this.props.value}
                problems={settingsProblems(schema, this.props.value)}
                onChangeAt={this.handleChangeAt}
                onRemoveKeys={this.handleRemoveKeys}
                disabled={Boolean(field.onlyRead) || Boolean(this.props.disabled)}
                showAll={Boolean(this.props.error)}
                scenarios={this.scenarios()}
                store={this.store()}
                idPrefix={field.id}
                editorPrefix={`gateway-settings/${data?.id ?? 'new'}`}
            />
        );
    }
}
